import React, { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  DrawerBody,
  DrawerFooter,
  LoadingButton,
} from '@one-thd/sui-atomic-components';
import {
  useForm,
  SubmitHandler,
  FormProvider,
  useFormContext,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLazyDataModel } from '@thd-nucleus/data-sources';
import { InstantCheckoutDrawerHeader as DrawerHeader } from '../../../InstantCheckoutDrawerHeader';
import { AddressFormFields } from './AddressFormFields';
import { TAddressFormValues } from '../../../../../models/common/address';
import { validationSchema } from '../../../../../utils/validation-rules/address-validation';
import {
  ERR_ADDRESS_LIMIT,
  ERR_COULDNT_SAVE,
  ERR_REDIRECT,
} from '../../../../../constants/response-error-messages';
import { track } from '../../../../../analytics/analytics';
import {
  INSTANT_CHECKOUT_BUTTON_SUBMISSION,
  INSTANT_CHECKOUT_OVERLAY,
  POST_PAGE_ERROR,
} from '../../../../../constants/analytics';
import { ADDRESS_FORM_DATA_TEST_IDS } from '../../../../../constants/address-constants';
import { useDrawer } from '../../../../../hooks/use-drawer';
import {
  addAddresses,
  orangePayToken,
  updateInstantCheckoutSettings,
} from '../../../../../models/graphQLModels';
import { getChannelID, redirectToSignIn } from '../../../../../utils/general';
import { useInstantCheckoutStatus } from '../../../../../hooks/use-instant-checkout-status';
import { IAddress, IPhone } from '../../../../../models/address';
import { useAddress } from '../../../../../hooks/use-address';
import { NON_DIGITS_REGEX } from '../../../../../constants/regex';
import {
  ADD_DELIVERY_ADDRESS,
  ADD_PAYMENT,
  CONFIRM_PAYMENT,
  SELECT_PAYMENT,
} from '../../../../../models/views';
import { useViews } from '../../../../../hooks/use-views';
import { usePayments } from '../../../../../hooks/use-payments';
import { TICPaymentFormValues } from '../../../../../models/payments';
import {
  createNewRelicActions,
  logNewRelicAction,
} from '../../../../../utils/newrelic';
import {
  getIsAuthError,
  hasMaxAddressError,
} from '../../../../../constants/error-codes';
import { TAlertStatus, TAlertType } from '../../../../../models/common/alert';

interface AddressFormFieldsProps {
  addressType: 'delivery' | 'billing' | string;
  setLoadAddBilling?: (value: boolean) => void;
  setLoadSelectBilling?: (value: boolean) => void;
  customGoBack?: () => void;
}

export const AddAddressView = ({
  setLoadAddBilling,
  addressType,
  customGoBack,
  setLoadSelectBilling,
}: AddressFormFieldsProps) => {
  const [hasGeneralError, setHasGeneralError] = useState<boolean>(false);
  const [hasAddressLimitError, setHasAddressLimitError] =
    useState<boolean>(false);

  const { goToView, currentView } = useViews();
  const { defaultPayment, hasPayments } = usePayments();
  const actions = createNewRelicActions(currentView);

  const { setValue } = useFormContext<TICPaymentFormValues>?.() ?? {};

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { hideDrawer } = useDrawer();
  const { userId, customerAccountId, setAlertMessage } =
    useInstantCheckoutStatus();
  const { hasAddresses, setSelectedAddress, refetchAddresses } = useAddress();

  const formMethods = useForm<TAddressFormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      isDefault: false,
      primaryPhone: '',
      addressLine1: '',
      zipCode: '',
      cityState: '',
    },
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const { handleSubmit } = formMethods;

  const [callAddAddress, data] = useLazyDataModel('addAddresses');
  const { called, loading: addLoading, error, data: addResult } = data;

  useEffect(() => {
    setHasGeneralError(!!error);
    setHasAddressLimitError(false);
    setIsSubmitting(addLoading);
    if (addResult && called && !addLoading && !error) {
      const returnedAddress = addResult?.addAddresses?.addresses[0];
      const address: IAddress = {
        id: returnedAddress?.id,
        isDefault: returnedAddress?.isDefault,
        isInstantCheckoutDefault: returnedAddress?.isInstantCheckoutDefault,
        nickName: returnedAddress?.nickName,
        lastModifiedDate: returnedAddress?.lastModifiedDate,
        name: {
          firstName: returnedAddress?.name?.firstName,
          lastName: returnedAddress?.name?.lastName,
        },
        phones: returnedAddress?.phones?.map((phone: IPhone): IPhone => {
          return {
            number: phone?.number,
            id: phone?.id,
            isPrimary: phone?.isPrimary ?? false,
          };
        }),
        postalAddress: {
          addressLine1: returnedAddress?.postalAddress?.addressLine1,
          addressLine2: returnedAddress?.postalAddress?.addressLine2,
          city: returnedAddress?.postalAddress?.city,
          country: returnedAddress?.postalAddress?.country,
          stateProvince: returnedAddress?.postalAddress?.stateProvince,
          zipCode: returnedAddress?.postalAddress?.zipCode,
        },
      };
      logNewRelicAction(actions.ADD_ADDRESS);
      refetchAddresses();
      if (currentView === ADD_DELIVERY_ADDRESS) {
        setSelectedAddress(address);
        if (defaultPayment) {
          goToView(CONFIRM_PAYMENT);
        } else if (hasPayments()) {
          track(INSTANT_CHECKOUT_OVERLAY, {
            value: 'instant checkout select payment method',
          });
          goToView(SELECT_PAYMENT);
        } else {
          track(INSTANT_CHECKOUT_OVERLAY, {
            value: 'instant checkout add new payment',
          });
          goToView(ADD_PAYMENT);
        }
      } else {
        setValue?.('billingAddressID', address.id);
        setLoadAddBilling?.(false);
        setLoadSelectBilling?.(false);
      }
    }
    if (error) {
      logNewRelicAction(actions.ADD_ADDRESS, error);
      if (getIsAuthError(error)) {
        setAlertMessage({
          alertType: TAlertType.GENERAL,
          alertStatus: TAlertStatus.INFO,
          text: ERR_REDIRECT.message,
        });
        redirectToSignIn();
      }
      if (hasMaxAddressError(error)) {
        setHasGeneralError(false);
        setHasAddressLimitError(true);
      }

      track(POST_PAGE_ERROR, {
        pageSection: 'add address overlay',
        message: ERR_COULDNT_SAVE.message,
        code: error?.code || 'n/a',
        type: 'error',
        displayType: 'inline',
      });
    }
  }, [
    error,
    addLoading,
    addResult,
    called,
    defaultPayment,
    hasPayments,
    goToView,
    currentView,
    setSelectedAddress,
    refetchAddresses,
    setValue,
    setLoadAddBilling,
    setLoadSelectBilling,
    setAlertMessage,
    actions.ADD_ADDRESS,
  ]);

  const onSubmitForm: SubmitHandler<TAddressFormValues> = (formValues) => {
    track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
      overlayType: `add new ${addressType} address`,
      action: 'save and continue',
    });

    setIsSubmitting(true);

    const cityState = formValues.cityState.split(',');
    const city = cityState[0].trim();
    const state = cityState[1].trim();
    const phone = formValues.primaryPhone.replace(NON_DIGITS_REGEX, '');

    const newAddress: IAddress = {
      isDefault: !hasAddresses(),
      isInstantCheckoutDefault: false,
      postalAddress: {
        addressLine1: formValues.addressLine1,
        addressLine2: formValues.addressLine2 || null,
        city,
        stateProvince: state,
        zipCode: formValues.zipCode,
        country: 'US',
      },
      name: {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
      },
      phones: [{ number: phone, isPrimary: true }],
    };
    callAddAddress({
      variables: {
        userId,
        customerAccountId,
        channelId: getChannelID(),
        addresses: [newAddress],
      },
      ssr: false,
      context: {
        withAuth: true,
      },
    });
  };

  const onCancel = () => {
    track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
      overlayType: `add new ${addressType} address`,
      action: 'cancel',
    });
    if (addressType === 'billing') {
      setLoadAddBilling?.(false);
    } else {
      hideDrawer();
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <DrawerHeader
          title={`Add New ${
            addressType === 'delivery' ? 'Delivery' : 'Billing'
          } Address`}
          onClose={() => {
            hideDrawer();
            track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
              overlayType: `add new ${addressType} address`,
              action: 'cancel',
            });
          }}
          customGoBack={customGoBack}
        />
        <DrawerBody>
          <AddressFormFields />
          {hasGeneralError && (
            <Alert status="error">{ERR_COULDNT_SAVE.message}</Alert>
          )}
          {hasAddressLimitError && (
            <Alert status="warning">{ERR_ADDRESS_LIMIT.message}</Alert>
          )}
        </DrawerBody>

        <DrawerFooter>
          <div className="sui-flex sui-gap-2">
            <Button
              data-testid={ADDRESS_FORM_DATA_TEST_IDS.CANCEL}
              fullWidth
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <LoadingButton
              data-testid={ADDRESS_FORM_DATA_TEST_IDS.SAVE_AND_CONTINUE}
              type="submit"
              loading={isSubmitting}
              fullWidth
            >
              Save and Continue
            </LoadingButton>
          </div>
        </DrawerFooter>
      </form>
    </FormProvider>
  );
};

AddAddressView.dataModel = {
  addAddresses,
  orangePayToken,
  updateInstantCheckoutSettings,
};
